import { styled } from "goober";
import { type RefObject, useLayoutEffect, useRef } from "react";

const Overlay = styled("div")`
  position: absolute;
  margin: 0;
  border: 0;
  pointer-events: none;
  box-sizing: border-box;
  white-space: pre-wrap;
  overflow: hidden;
  color: var(--text-primary);
`;

const COPIED: readonly string[] = [
  "fontFamily",
  "fontSize",
  "fontWeight",
  "fontStyle",
  "fontVariant",
  "letterSpacing",
  "lineHeight",
  "textAlign",
  "textIndent",
  "textTransform",
  "wordSpacing",
  "tabSize",
  "overflowWrap",
  "wordBreak",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
];

type Props = {
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  value: string;
  caret: number;
  suffix: string;
};

export function GhostText({ textareaRef, value, caret, suffix }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const ta = textareaRef.current;
    const el = ref.current;
    if (!ta || !el) return;

    const sync = () => {
      const cs = getComputedStyle(ta) as unknown as Record<string, string>;
      const style = el.style as unknown as Record<string, string>;
      for (const p of COPIED) style[p] = cs[p]!;

      const parent = el.offsetParent as HTMLElement | null;
      const prect = parent?.getBoundingClientRect();
      const rect = ta.getBoundingClientRect();
      el.style.top = `${rect.top - (prect?.top ?? 0)}px`;
      el.style.left = `${rect.left - (prect?.left ?? 0)}px`;
      el.style.width = `${ta.clientWidth}px`;
      el.style.height = `${ta.clientHeight}px`;
      el.scrollTop = ta.scrollTop;
      el.scrollLeft = ta.scrollLeft;
    };

    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(ta);
    ta.addEventListener("scroll", sync);
    return () => {
      ro.disconnect();
      ta.removeEventListener("scroll", sync);
    };
  }, [textareaRef, value, caret, suffix]);

  if (!suffix) return null;

  return (
    <Overlay ref={ref} aria-hidden="true">
      {value.slice(0, caret)}
      {/* The <wbr> lets the typed text wrap as a normal word (staying aligned with the textarea) while the completion breaks freely to fill whatever line it lands on. */}
      <wbr />
      <span
        style={{
          color: "var(--text-primary)",
          opacity: 0.3,
          wordBreak: "break-all",
        }}
      >
        {suffix}
      </span>
      {value.slice(caret)}
    </Overlay>
  );
}
